import { useContext, useEffect, useState } from "react";
import { Button, Form, Select, Row, Col, Space, Tooltip } from "antd";
import Input from "../../../components/input";
import FormLayout, {
  FormContent,
  FormFooter,
  FormHeader,
} from "../../../components/form/form";
import { FormRow } from "../../../components/form/form-row";
import {
  TConfigParam,
  TDropdownOptions,
  TMenu,
  TPrivilege,
  TResponseModel,
  TRole,
} from "../../../types/config";
import { eResultCode, eRoleUniqueId } from "../../../utils/enum";
import useFetch from "../../../hooks/useFetch";
import useToast from "../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import PrivilegeMapper from "../../../components/privilege-mapper/index";
import {
  AddOrUpdateRoleInformation,
  GetConfigParamDataList,
  GetMenuHierarchyList,
  GetSpecificRoleInformation,
} from "../../../utils/api.constant";
import {
  CONFIG_GROUPS,
  PageSize,
  ROLEUNIQUEID,
  STAKE_HOLDERS,
} from "../../../utils/constants";
import Utils from "../../../utils";
import { requiredFieldValidator } from "../../../utils/input.validator";
import { UserContext } from "../../../context";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import RoleTemplateSelector, {
  RoleTemplate,
} from "../../../components/role-template-selector/index";

type TDrawerProps = {
  id: number;
  isOpen?: any;
  onCloseDrawer: () => void;
  onRefreshList: () => void;
};

/**
 * This component is used to create/update roles and assign the privileges to the role.
 * This component is used in drawer and it takes the role id as a prop.
 * If the role id is present then it fetches the role details and populate the form.
 * If the role id is not present then it will render the form with empty fields.
 * The form has the fields like role name, role unique id, stake holder, description and privileges.
 * The privileges field is a custom component which is used to assign the privileges to the role.
 * This component is a form which is connected to the antd form component.
 * The form has the validation for the required fields.
 * The form has the submit button which will call the onFinish function when clicked.
 * The onFinish function will call the API to create or update the role and assign the privileges to the role.
 * The onFinish function will also call the onRefreshList function to refresh the list of roles.
 * The onFinish function will also close the drawer.
 */

export default function RoleForm(props: TDrawerProps) {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [menuHierarchy, setMenuHierarchy] = useState<TMenu[]>([]);
  const [rolePrivileges, setRolePrivileges] = useState<TPrivilege[]>([]);
  const [specificRoleData, setSpecificRoleData] = useState<any>();
  const { post } = useFetch();
  const { onShowToast } = useToast();
  // const [stakeHolderOptions, setStakeHolderOptions] = useState<
  //   TDropdownOptions[]
  // >([]);
  const [selectedStakeHolder, setSelectedStakeHolder] = useState<string>();
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>();
  const context: any = useContext(UserContext);

  // Hook to handle on load operations
  useEffect(() => {
    const fetchData = async () => {
      await fetchSpecificRole();
      await getMenuHierarchyList();
    };
    fetchData();
  }, []);

  // Function to fetch menu hierarchy list from the server
  const getMenuHierarchyList = async () => {
    try {
      const requestPayload = {
        data: {
          renderMenuRoleWise: false,
        },
      };

      const response = await post(GetMenuHierarchyList, requestPayload);
      const { data, dataResponse } = response;
      const { returnCode, description } = dataResponse;
      if (returnCode === eResultCode.SUCCESS) {
        setMenuHierarchy(data);
      } else {
        console.error(`Error while fetching menu hierarchy: ${description}`);
      }
    } catch (error) {
      console.error(error);
    }
  };

  // To get the list of parameter options
  const fetchConfigParams = async (groupUniqueId: string) => {
    try {
      const payload = {
        data: {
          groupUniqueId,
          pageSize: PageSize,
        },
      };

      const response: TResponseModel = await post(
        GetConfigParamDataList,
        payload,
      );
      if (response) {
        const { dataResponse, data } = response;
        const { returnCode, description } = dataResponse;
        if (returnCode === eResultCode.SUCCESS) {
          const updatedOptions = data.filter((option: TConfigParam) => {
            if (context?.user?.stakeHolderName === STAKE_HOLDERS.CUSTOMER) {
              return option.paramUniqueId === "CUSTOMER";
            } else if (
              context?.user?.stakeHolderName === STAKE_HOLDERS.SERVICEPARTNER
            ) {
              return option.paramUniqueId === "SERVICEPARTNER";
            } else if (context?.user?.stakeHolderName === STAKE_HOLDERS.IDEX) {
              return true; // Include all options for IDEX
            }
            return false; // Default case if none match
          });

          return Utils.mapDropdownOptions(updatedOptions);
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: description,
          });
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
    }
  };

  const defaultValues: TRole = {
    id: 0,
    name: "",
    description: "",
    roleUniqueId: "",
    // stakeHolderId: null,
    // stakeHolderName: "",
  };

  // to get the list of checked privileges
  const handlePrivilegesChange = (checkedPrivileges: TPrivilege[]) => {
    setRolePrivileges(checkedPrivileges);
  };

  // Handle template selection
  const handleTemplateSelect = (template: RoleTemplate) => {
    setSelectedTemplateId(template.id);

    if (template.id === "custom") {
      // Don't auto-select any privileges for custom
      return;
    }

    // Find all matching privileges from menuHierarchy
    const selectedPrivileges: TPrivilege[] = [];

    const findPrivileges = (menus: TMenu[]) => {
      menus.forEach((menu) => {
        if (menu.privileges) {
          menu.privileges.forEach((privilege: any) => {
            if (template.privilegeIds.includes(privilege.privilegeUniqueId)) {
              selectedPrivileges.push(privilege);
            }
          });
        }
        if (menu.children && menu.children.length > 0) {
          findPrivileges(menu.children);
        }
      });
    };

    findPrivileges(menuHierarchy);
    setRolePrivileges(selectedPrivileges);
  };

  // Handle on submit event
  const onFinish = async (values: TRole) => {
    try {
      const payload = {
        data: {
          id: props?.id ? props?.id : 0,
          ...values,
          rolePrivileges: rolePrivileges,
        },
      };
      console.log(payload);

      setIsLoading(true);

      const response = await post(AddOrUpdateRoleInformation, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
        setRolePrivileges([]);
        form.resetFields();
        props.onRefreshList();
        props.onCloseDrawer();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSpecificRole = async () => {
    if (props.id > 0) {
      try {
        const payload = {
          data: {
            id: props.id,
          },
        };
        setIsLoading(true);
        const response: TResponseModel = await post(
          GetSpecificRoleInformation,
          payload,
        );
        const { data, dataResponse } = response;
        const { description, returnCode } = dataResponse;

        if (returnCode === eResultCode.SUCCESS) {
          if (data && typeof data === "object") {
            setSpecificRoleData(data);
            form.setFieldsValue({
              id: data.id || 0,
              name: data.name || "",
              description: data.description || "",
              roleUniqueId: data.roleUniqueId || "",
            });
            const { rolePrivileges, ...restDetails } = data;
            setRolePrivileges(rolePrivileges);
          } else {
            console.error("Unexpected response structure:", data);
          }
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: description,
          });
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <FormLayout>
      <Form
        form={form}
        name="register"
        onFinish={onFinish}
        initialValues={defaultValues}
        layout="vertical"
      >
        <FormHeader>
          <CloseOutlined
            style={{ cursor: "pointer", marginTop: "0.3rem", color: "white" }}
            onClick={() => {
              props.onCloseDrawer();
            }}
          />
        </FormHeader>

        <FormContent>
          {/* <Row gutter={16} className="">
            <Space>
              <Col span={24}>
                <h2 style={{ color: "#fff", marginTop: "10px" }}>
                  {`${props.id ? "Edit" : "Add"} a Role`}
                </h2>
              </Col>
            </Space>
          </Row> */}
          <Row gutter={16} className="formRowHeight">
            <Col span={12}>
              <Form.Item
                name="name"
                label="Role Name"
                rules={[
                  {
                    required: true,
                    validator: requiredFieldValidator("enter role name"),
                  },
                ]}
              >
                <Input
                  placeholder="Enter role name"
                  disabled={
                    specificRoleData?.roleUniqueId === eRoleUniqueId.ADMIN
                  }
                />
              </Form.Item>
            </Col>

            {/* <Col span={12}>
              <Form.Item
                name='roleUniqueId'
                label='Role Unique Id'
                rules={[
                  {
                    required: true,
                    validator: requiredFieldValidator("enter role unique id"),
                  },
                ]}
              >
                <Input
                  disabled={props?.id ? true : false}
                  placeholder='Role unique id'
                />
              </Form.Item>
            </Col> */}
          </Row>

          <Row gutter={16} className="formRowHeight">
            <Col span={24}>
              <Form.Item name="description" label="Description">
                <Input
                  placeholder="Description"
                  disabled={
                    specificRoleData?.roleUniqueId === eRoleUniqueId.ADMIN
                  }
                />
              </Form.Item>
            </Col>
          </Row>

          <RoleTemplateSelector
            onTemplateSelect={handleTemplateSelect}
            selectedTemplateId={selectedTemplateId}
          />

          <PrivilegeMapper
            menuHierarchy={menuHierarchy}
            preSelectedPrivileges={rolePrivileges}
            onPrivilegesChange={handlePrivilegesChange}
          ></PrivilegeMapper>
        </FormContent>

        <FormFooter className="formFooter">
          <Button
            className="btnDimensions"
            type="default"
            disabled={isLoading}
            onClick={props.onCloseDrawer}
          >
            Cancel
          </Button>
          <Tooltip
            title="You can't edit Admin role details."
            zIndex={
              specificRoleData?.roleUniqueId === eRoleUniqueId.ADMIN ? 9999 : -1
            }
          >
            <Button
              className="btnDimensions"
              type="primary"
              onClick={form.submit}
              disabled={
                isLoading ||
                specificRoleData?.roleUniqueId === eRoleUniqueId.ADMIN
              }
            >
              {props.id ? "Update" : "Add"}
            </Button>
          </Tooltip>
        </FormFooter>
      </Form>
    </FormLayout>
  );
}
